import React, {
  FunctionComponent,
  useState,
  useEffect,
  SyntheticEvent,
  useContext,
} from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  ActionType,
  PrescriptionDrug,
  DrugDosage,
  DrugPackage,
  PrescriptionFrequency,
  PharmacyType,
} from "../../@types";
import { getDrugDosages } from "../../api";
import { Button, Dialog, Dropdown, TextField } from "@cmsgov/ds-medicare-gov";
import { useTranslate } from "../../helpers/intlHooks";
import { AppStore } from "../../app/store";
import {
  displayPackage,
  displayDrug,
  uppercaseFirst,
  getPrescriptionFrequencyLabels,
} from "../../helpers/prescriptionHelpers";
import { logError, sendTealiumEvent } from "../../helpers";
import { PrescriptionsView } from "./ManagePrescriptionsPage";

interface EditPrescriptionProps {
  drug: { is_generic: boolean; rxcui: string; name: string };
  prescriptionIndex: number;
  viewList: (clearRecentlyAdded: boolean) => void;
  resetPrescriptionIndex: () => void;
  goToPharmacySelection: () => void;
  resetModeOnError: () => void;
  addPrescriptions: () => void;
  previousMode: PrescriptionsView;
}

const EditPrescription: FunctionComponent<EditPrescriptionProps> = ({
  drug,
  prescriptionIndex,
  viewList,
  resetPrescriptionIndex,
  goToPharmacySelection,
  resetModeOnError,
  addPrescriptions,
  previousMode,
}) => {
  const t = useTranslate();
  const { dispatch, state } = useContext(AppStore.AppContext);
  const { tmpFeEnableNewDrugFrequencies } = useFlags();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dosage, setDosage] = useState({} as DrugDosage);
  const [dosages, setDosages] = useState({} as DrugDosage[]);
  const [pack, setPack] = useState({} as DrugPackage);
  const [prescription, setPrescription] = useState({
    frequency:
      state.pharmacyType === PharmacyType.MAIL
        ? PrescriptionFrequency.DAYS_90
        : PrescriptionFrequency.DAYS_30,
    quantity: "",
  } as Partial<PrescriptionDrug>);
  const [drugFrequencies, setDrugFrequencies] = useState<
    { label: string; value: string }[]
  >([]);

  useEffect(() => {
    if (state.year) {
      const frequencyLabels = getPrescriptionFrequencyLabels(
        tmpFeEnableNewDrugFrequencies,
        state.year
      );

      const frequencies = Object.keys(frequencyLabels).map(key => ({
        label: t(frequencyLabels[key]),
        value: key,
      }));

      setDrugFrequencies(frequencies);
    }
  }, [tmpFeEnableNewDrugFrequencies, state.year]);

  const getDefaultQuantity = (pack: DrugPackage): number | "" => {
    const defaultQuantity =
      state.pharmacyType === PharmacyType.MAIL
        ? pack.default_quantity_90
        : pack.default_quantity_30;

    return defaultQuantity || "";
  };

  const setDrugPackage = (
    pack: DrugPackage,
    useDefaultQuantity = true
  ): void => {
    setPack(pack);

    if (useDefaultQuantity) {
      setPrescription({
        ...prescription,
        quantity: getDefaultQuantity(pack),
      });
    }
  };

  useEffect(() => {
    setLoading(true);
    setError(false);
    getDrugDosages(drug.rxcui, state.year)
      .then(result => {
        if (result) {
          setLoading(false);
          setDosages(result.dosages);

          if (prescriptionIndex !== -1) {
            const prescription = state.prescriptions[prescriptionIndex];
            setPrescription(prescription);

            const dosage = result.dosages.find(
              d =>
                d.form === prescription.form &&
                d.strength === prescription.strength &&
                d.strength_units === prescription.strength_units &&
                d.rxcui === prescription.rxcui
            );
            setDosage(dosage || result.dosages[0]);
          } else if (result.commonDosage) {
            setDosage(result.commonDosage);
            const pack = result.commonDosage.packages[0];

            setDrugPackage(pack);
          }
        } else {
          setLoading(false);
          setError(true);
          throw "API Error";
        }
      })
      .catch(e => {
        setError(true);
        logError("Could not get drug dosages (EditPrescription)", e);
      });
  }, []);

  const showDosagePackages =
    dosage &&
    dosage.packages &&
    dosage.packages.length &&
    dosage.packages.every(p => p.use_quantity);

  useEffect(() => {
    if (dosage && dosage.packages && dosage.packages.length) {
      if (prescriptionIndex === -1) {
        setDrugPackage(dosage.packages[0]);
      } else {
        const pack = dosage.packages.find(p => p.ndc === prescription.ndc);

        setDrugPackage(pack || dosage.packages[0], false);
      }
    }
  }, [dosage]);

  const makeDosageValueString = (dosage: DrugDosage): string =>
    dosage.form && dosage.rxcui
      ? `${dosage.strength || ""}|${dosage.strength_units || ""}|${
          dosage.form
        }|${dosage.rxcui}`
      : "";

  if (loading) {
    return <div className="ds-u-margin-y--6">{t("app.loading")}...</div>;
  } else if (!dosage || error) {
    return (
      <Dialog
        onExit={(): void => viewList(false)}
        heading={t("prescription.error_dialog_title")}
        headerClassName="AddPrescription__promptHeader"
        actions={[
          <Button
            variation="primary"
            size="big"
            key="add-generic-button"
            onClick={(): void => {
              // istanbul ignore next: tested elsewhere
              sendTealiumEvent("Find Plans - Add Drugs", "Add Another Drug");
              resetModeOnError();
            }}
          >
            {t("prescription.add_other_drugs")}
          </Button>,
          <Button
            key="add-brand-button"
            variation="transparent"
            onClick={(): void => goToPharmacySelection()}
          >
            {t("continue")}
          </Button>,
        ]}
      >
        <p>{t("prescription.error_dialog_message")}</p>
      </Dialog>
    );
  } else {
    return (
      <form className="EditPrescription e2e-drug-form">
        <h2 className="ds-u-margin--0 ds-u-margin-top--1 ds-u-margin-bottom--4">
          {uppercaseFirst(drug.name)}
        </h2>

        {!!dosages.length && (
          <Dropdown
            label={t("prescription.dosage")}
            id="drug-select-dosage"
            name="dosage"
            fieldClassName="e2e-drug-select-dosage"
            required
            onChange={(event: SyntheticEvent<HTMLSelectElement>): void => {
              const dosageString = event.currentTarget.value;
              const [
                strength,
                strength_units,
                form,
                rxcui,
              ] = dosageString.split("|");

              const dosage = dosages.find(
                d =>
                  d.form === form &&
                  d.strength === strength &&
                  d.strength_units === strength_units &&
                  d.rxcui === rxcui
              );

              setDosage(dosage || ({} as DrugDosage));
            }}
            value={makeDosageValueString(dosage)}
            options={[]}
          >
            <option value="">{t("prescription.select_dosage")}</option>
            {dosages.map((d, index) => (
              <option
                value={makeDosageValueString(d)}
                className={`e2e-drug-dosage-option-${index}`}
                key={`${d.rxcui}-${index}`}
              >
                {displayDrug(t, {
                  form: d.form,
                  strength: d.strength,
                  strength_units: d.strength_units,
                  name: d.has_duplicate_name ? d.name : "",
                })}
              </option>
            ))}
          </Dropdown>
        )}

        {showDosagePackages && (
          <Dropdown
            label={t("prescription.package")}
            id="drug-select-package"
            name="package"
            onChange={(event: SyntheticEvent<HTMLSelectElement>): void => {
              const pack = dosage.packages.find(
                p => p.ndc === event.currentTarget.value
              );

              setDrugPackage(pack || ({} as DrugPackage));
            }}
            value={pack.ndc}
            options={[]}
          >
            {dosage.packages.map(dosagePack => (
              <option value={dosagePack.ndc} key={dosagePack.ndc}>
                {displayPackage(t, {
                  form: dosage.form,
                  package_size: dosagePack.size.toString(10),
                  package_units: dosagePack.unit,
                  package_quantity: dosagePack.quantity.toString(10),
                  package_type: dosagePack.type,
                })}
              </option>
            ))}
          </Dropdown>
        )}

        <div className="ds-u-display--flex ds-u-align-items--end">
          <TextField
            label={t("prescription.quantity")}
            id="drug-select-quantity"
            name="quantity"
            value={prescription.quantity}
            className="e2e-drug-quantity"
            numeric
            min="1"
            size="small"
            required
            onChange={(event: SyntheticEvent<HTMLInputElement>): void => {
              const { value } = event.currentTarget;

              const parsedValue = parseInt(value, 10);

              const quantity = isNaN(parsedValue) ? "" : Math.abs(parsedValue);

              setPrescription({
                ...prescription,
                quantity,
              });
            }}
          />
          <div className="ds-u-clearfix">
            <Dropdown
              label={t("prescription.frequency")}
              id="drug-select-frequency"
              value={prescription.frequency}
              className="ds-u-margin-left--3"
              name="frequency"
              onChange={(event: SyntheticEvent<HTMLSelectElement>): void =>
                setPrescription({
                  ...prescription,
                  frequency: event.currentTarget.value as PrescriptionFrequency,
                })
              }
              options={drugFrequencies}
            />
          </div>
        </div>

        <Button
          variation="transparent"
          onClick={(): void => {
            if (previousMode === PrescriptionsView.ADD) {
              addPrescriptions();
            } else {
              viewList(true);
            }
          }}
          className="ds-c-button--primary-alt EditPrescription__cancelButton"
        >
          {t("cancel")}
        </Button>

        <div className="EditPrescription__footer">
          <Button
            variation="primary"
            type="submit"
            className="e2e-drug-add-to-list mct-u-full-width-mobile"
            size="big"
            onClick={(
              event: React.MouseEvent<HTMLButtonElement, MouseEvent>
            ): boolean => {
              if (!dosage.rxcui || !prescription.quantity) {
                return false;
              }

              // This is needed to prevent Microsoft Edge from redirecting on form submit
              event.preventDefault();

              // istanbul ignore next: tested elsewhere
              sendTealiumEvent("Find Plans - Add Drugs", "Add Drug to List");

              const drugPayload: PrescriptionDrug = {
                rxcui: dosage.rxcui,
                name: dosage.has_duplicate_name ? dosage.name : drug.name,
                ndc: pack.ndc,
                form: dosage.form,
                strength: dosage.strength,
                strength_units: dosage.strength_units,
                package_type: pack.type,
                package_size: pack.size.toString(10),
                package_units: pack.unit,
                frequency: prescription.frequency as Required<
                  PrescriptionFrequency
                >,
                package_quantity: pack.quantity.toString(10),
                quantity: prescription.quantity,
                is_generic: drug.is_generic,
                use_package_quantity: pack.use_quantity,
              };

              if (prescriptionIndex === -1) {
                dispatch({
                  type: ActionType.ADD_PRESCRIPTION,
                  payload: drugPayload,
                });
              } else {
                dispatch({
                  type: ActionType.EDIT_PRESCRIPTION,
                  payload: {
                    index: prescriptionIndex,
                    drug: drugPayload,
                  },
                });

                resetPrescriptionIndex();
              }

              viewList(false);
              return true;
            }}
          >
            {t("prescription.add_to_my_drug_list")}
          </Button>
        </div>
      </form>
    );
  }
};

export default EditPrescription;
